import { Pagination } from '@ekkogmbh/apisdk';
import { createContextHardened } from '../Helper/ContextHelper';
import { EventEmitter } from 'eventemitter3';
import { useCallback, useEffect, useRef, useState } from 'react';

interface Context {
  page: Pagination['page'];
  limit: Pagination['limit'];
  setPage: (page: number) => void;
  setLimit: (rows: number) => void;
  /** calls refresh on all listeners */
  refresh: () => void;
  /** register a callback to the refresh event. Make sure to unsubscribe later. */
  subscribeRefresh: (callback: (pagination: Pagination) => void) => void;
  unsubscribeRefresh: (callback: (pagination: Pagination) => void) => void;
}
const [PaginationContext, usePagination] = createContextHardened<Context>('Pagination');
export default usePagination;

interface Props {
  children: React.ReactNode;
}
export const PaginationProvider = ({ children }: Props): React.JSX.Element => {
  const isInitialRendering = useRef<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(100);

  const refreshEmitterRef = useRef<EventEmitter<'refresh'>>(new EventEmitter<'refresh'>());
  const paginationRef = useRef<Pagination>({ page, limit });

  useEffect(() => {
    paginationRef.current = { page, limit };
  }, [page, limit]);

  /**
   * Triggers a refresh on page/limit changes.
   * Filter and sort changes are handled by DataTableProvider/DataTable.
   */
  useEffect(() => {
    if (isInitialRendering.current) {
      isInitialRendering.current = false;
      return;
    }
    refreshEmitterRef.current.emit('refresh', paginationRef.current);
  }, [page, limit]);

  // registers a simple logging listener
  useEffect(() => {
    refreshEmitterRef.current.on('refresh', (pagination) => {
      console.log('[PAGINATION] refresh triggered', pagination);
    });
    return () => {
      refreshEmitterRef.current.removeAllListeners();
    };
  }, []);

  const refresh = useCallback(() => refreshEmitterRef.current.emit('refresh', paginationRef.current), []);

  return (
    <PaginationContext.Provider
      value={{
        page,
        limit,
        setPage,
        setLimit,
        refresh,
        subscribeRefresh: (callback) => {
          refreshEmitterRef.current.off('refresh', callback);
          refreshEmitterRef.current.on('refresh', callback);
        },
        unsubscribeRefresh: (callback) => refreshEmitterRef.current.off('refresh', callback),
      }}
    >
      {children}
    </PaginationContext.Provider>
  );
};
